/**
 * Validation rules for the bank account payment method.
 *
 * Standards used:
 *  - SWIFT / BIC: ISO 9362 — 8 or 11 characters: 4-letter bank code,
 *    2-letter ISO 3166 country code, 2 alphanumeric location code,
 *    optional 3 alphanumeric branch code.
 *  - US routing number (ABA RTN): 9 digits with the ABA checksum
 *    3(d1+d4+d7) + 7(d2+d5+d8) + (d3+d6+d9) ≡ 0 (mod 10).
 *  - IBAN: ISO 13616 — 2-letter country code, 2 check digits, up to 30
 *    alphanumeric BBAN characters (15–34 total), mod-97 check equal to 1,
 *    and the registered length for known countries.
 *  - US account number: 4–17 digits (NACHA).
 *  - Countries in the IBAN registry must provide an IBAN whose country
 *    prefix matches the selected country; other countries: 5–34
 *    alphanumeric characters containing digits.
 *  - Postal code: 3–10 characters, letters/digits with optional space or
 *    hyphen (covers US ZIP/ZIP+4, UK, Canada, EU formats).
 */

export const US_COUNTRY_RE = /^(united states( of america)?|usa|u\.s\.a?\.?)$/i;
export const isUSCountry = (country) => US_COUNTRY_RE.test(String(country || "").trim());

export const normalizeCode = (value) =>
  String(value || "")
    .replace(/[\s-]/g, "")
    .toUpperCase();

// --- SWIFT / BIC (ISO 9362) --------------------------------------------------
export const BIC_RE = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
export const isValidBIC = (value) => BIC_RE.test(normalizeCode(value));

// --- ABA routing number ------------------------------------------------------
export const isValidABARouting = (value) => {
  const digits = normalizeCode(value);
  if (!/^\d{9}$/.test(digits)) return false;
  const d = digits.split("").map(Number);
  const sum =
    3 * (d[0] + d[3] + d[6]) + 7 * (d[1] + d[4] + d[7]) + (d[2] + d[5] + d[8]);
  return sum % 10 === 0;
};

// --- IBAN (ISO 13616) --------------------------------------------------------
// Registered IBAN lengths (IBAN Registry). Countries not listed only get the
// generic 15–34 length rule plus the mod-97 check.
export const IBAN_LENGTHS = {
  AD: 24, AE: 23, AL: 28, AT: 20, AZ: 28, BA: 20, BE: 16, BG: 22, BH: 22, BR: 29,
  BY: 28, CH: 21, CR: 22, CY: 28, CZ: 24, DE: 22, DK: 18, DO: 28, EE: 20, EG: 29,
  ES: 24, FI: 18, FO: 18, FR: 27, GB: 22, GE: 22, GI: 23, GL: 18, GR: 27, GT: 28,
  HR: 21, HU: 28, IE: 22, IL: 23, IQ: 23, IS: 26, IT: 27, JO: 30, KW: 30, KZ: 20,
  LB: 28, LC: 32, LI: 21, LT: 20, LU: 20, LV: 21, LY: 25, MC: 27, MD: 24, ME: 22,
  MK: 19, MR: 27, MT: 31, MU: 30, NL: 18, NO: 15, PK: 24, PL: 28, PS: 29, PT: 25,
  QA: 29, RO: 24, RS: 22, SA: 24, SC: 31, SD: 18, SE: 24, SI: 19, SK: 24, SM: 27,
  ST: 25, SV: 28, TL: 23, TN: 24, TR: 26, UA: 29, VA: 22, VG: 24, XK: 20,
};

export const IBAN_RE = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;

/** Looks like an IBAN (country code + check digits) even if it fails the checks. */
export const looksLikeIBAN = (value) => /^[A-Z]{2}\d{2}/.test(normalizeCode(value));

const mod97 = (numericString) => {
  let remainder = 0;
  for (let i = 0; i < numericString.length; i += 7) {
    const chunk = `${remainder}${numericString.slice(i, i + 7)}`;
    remainder = parseInt(chunk, 10) % 97;
  }
  return remainder;
};

export const isValidIBAN = (value) => {
  const iban = normalizeCode(value);
  if (!IBAN_RE.test(iban)) return false;
  const expected = IBAN_LENGTHS[iban.slice(0, 2)];
  if (expected && iban.length !== expected) return false;
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const numeric = rearranged.replace(/[A-Z]/g, (ch) => String(ch.charCodeAt(0) - 55));
  return mod97(numeric) === 1;
};

// --- Account number ----------------------------------------------------------
export const US_ACCOUNT_RE = /^\d{4,17}$/;
// Non-IBAN countries: 5–34 letters/digits, must contain a digit
export const GENERIC_ACCOUNT_RE = /^(?=.*\d)[A-Z0-9]{5,34}$/;

export const usesIBAN = (countryCode) => !!IBAN_LENGTHS[String(countryCode || "").toUpperCase()];

/** Resolves the ISO alpha-2 code for a country name from the countries list. */
export const resolveCountryCode = (countryName, countries = []) => {
  const name = String(countryName || "").trim().toLowerCase();
  if (!name) return "";
  const found = (countries || []).find(
    (c) => typeof c === "object" && c && String(c.name || "").trim().toLowerCase() === name
  );
  if (found?.code) return String(found.code).toUpperCase();
  return isUSCountry(countryName) ? "US" : "";
};

/** What the account field expects for the country, shown as a hint. */
export const accountNumberHint = (countryCode) => {
  const code = String(countryCode || "").toUpperCase();
  if (code === "US") return "US account number: 4–17 digits";
  if (usesIBAN(code)) return `IBAN required: starts with ${code}, ${IBAN_LENGTHS[code]} characters`;
  return "Account number: 5–34 letters or digits (IBAN if your bank provides one)";
};

/** What the routing field expects for the country, shown as a hint. */
export const routingNumberHint = (countryCode) => {
  const code = String(countryCode || "").toUpperCase();
  if (code === "US") return "US routing number: 9 digits";
  return "SWIFT / BIC: 8 or 11 characters (e.g. DEUTDEFF500)";
};

/**
 * Returns an error message or "" for the "Beneficiary Account Number / IBAN" field.
 * `countryCode` is the ISO alpha-2 code of the selected country.
 */
export const validateAccountNumber = (value, countryCode) => {
  const code = normalizeCode(value);
  const country = String(countryCode || "").toUpperCase();
  if (!code) return "Required field";
  if (country === "US") {
    return US_ACCOUNT_RE.test(code) ? "" : "US account number must be 4–17 digits";
  }
  if (usesIBAN(country)) {
    if (!code.startsWith(country)) return `IBAN for this country must start with ${country}`;
    if (code.length !== IBAN_LENGTHS[country]) {
      return `IBAN for this country must be ${IBAN_LENGTHS[country]} characters`;
    }
    return isValidIBAN(code) ? "" : "Invalid IBAN (check digits do not match)";
  }
  if (looksLikeIBAN(code)) {
    return isValidIBAN(code) ? "" : "Invalid IBAN (check the country code, length and digits)";
  }
  return GENERIC_ACCOUNT_RE.test(code)
    ? ""
    : "Account number must be 5–34 letters or digits and contain digits";
};

/**
 * Returns an error message or "" for the "SWIFT / BIC or Routing Number" field.
 * `countryCode` is the ISO alpha-2 code of the selected country.
 */
export const validateRoutingNumber = (value, countryCode) => {
  const code = normalizeCode(value);
  const country = String(countryCode || "").toUpperCase();
  if (!code) return "Required field";
  if (country === "US") {
    return isValidABARouting(code) ? "" : "US routing number must be 9 digits with a valid checksum";
  }
  if (/^\d+$/.test(code)) {
    return country
      ? "Use the bank's SWIFT / BIC code for banks outside the US"
      : isValidABARouting(code)
      ? ""
      : "Routing number must be 9 digits with a valid checksum";
  }
  if (!isValidBIC(code)) return "SWIFT / BIC must be 8 or 11 characters (e.g. DEUTDEFF500)";
  if (country && code.slice(4, 6) !== country) {
    return `SWIFT / BIC country (${code.slice(4, 6)}) does not match the selected country (${country})`;
  }
  return "";
};

// --- Text fields -------------------------------------------------------------
// Latin letters incl. diacritics, spaces and common name punctuation.
export const NAME_RE = /^[A-Za-zÀ-ÖØ-öø-ÿ' .-]+$/;
export const CITY_RE = /^[A-Za-zÀ-ÖØ-öø-ÿ' .-]+$/;
export const STREET_RE = /^[A-Za-z0-9À-ÖØ-öø-ÿ' .,/#-]+$/;
export const POST_CODE_RE = /^[A-Za-z0-9]+([ -][A-Za-z0-9]+)?$/;

export const hasLetter = (value) => /[A-Za-zÀ-ÖØ-öø-ÿ]/.test(String(value || ""));

// --- Date of birth -----------------------------------------------------------
export const MIN_AGE = 18;
export const MAX_AGE = 120;

/** Returns an error message or "" for an ISO date string (YYYY-MM-DD). */
export const validateBirthDate = (value, now = new Date()) => {
  if (!value) return "Required field";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return "Invalid date";
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value) {
    return "Invalid date";
  }
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  if (date > today) return "Date of birth cannot be in the future";
  const min = new Date(today);
  min.setUTCFullYear(today.getUTCFullYear() - MIN_AGE);
  if (date > min) return `Beneficiary must be at least ${MIN_AGE} years old`;
  const max = new Date(today);
  max.setUTCFullYear(today.getUTCFullYear() - MAX_AGE);
  if (date < max) return "Invalid date of birth";
  return "";
};
